// Dutch (je) copy for /on-demand-ftp, translated from copy.en.ts. Same shape (checked by copy.test.ts).
// "FTP op aanvraag"; "de web-uploader" for the web uploader.
// The uploader's and FileZilla's own labels are in English, so «labels» are quoted as the app shows them.
import type { OnDemandFtpCopy } from "./copy.en";

const nl: OnDemandFtpCopy = {
  seo: {
    title: "Zo werkt FTP op aanvraag | Photohawk",
    description:
      "Is FTP echt nodig? Dan kan iedereen in je werkruimte vanuit de web-uploader een eigen Photohawk-FTP-server starten, bij alle abonnementen. Klaar in ongeveer 30 seconden, op een persoonlijk adres dat nooit verandert, en na een uur zonder activiteit gaat hij vanzelf uit.",
  },
  hero: {
    badge: "Alle abonnementen",
    eyebrow: "Nu beschikbaar",
    title: "Zo werkt FTP op aanvraag",
    subtitle:
      "Is FTP echt nodig? Start dan vanuit de web-uploader je eigen FTP-server, wanneer je hem nodig hebt. Hij is in ongeveer 30 seconden klaar en gaat vanzelf uit als je klaar bent.",
  },
  before: {
    title: "Voordat je begint: de web-uploader is sneller",
    body: "De web-uploader is de standaardmanier om te uploaden, bij alle abonnementen.",
    points: [
      { id: "parallel", text: "Hij verstuurt veel foto's tegelijk, veel meer dan een FTP-programma als FileZilla (standaard 2 tegelijk, en nooit meer dan 10)." },
      { id: "adaptive", text: "Hij past zich aan je verbinding aan, om er alles uit te halen." },
      { id: "live", text: "Previews gaan eerst, zodat je galerij binnen minuten online staat terwijl de originelen volgen." },
      { id: "resume", text: "Valt je verbinding weg, dan gaat hij vanzelf verder." },
    ],
    button: "Open de web-uploader",
    link: "Bekijk de nieuwe web-uploader",
  },
  what: {
    heading: "Je eigen FTP-server, wanneer je er een nodig hebt",
    body: "Iedereen in je werkruimte kan vanuit de web-uploader een eigen FTP-server starten, bij alle abonnementen. Iedereen krijgt een eigen server, op een persoonlijk adres dat nooit verandert, dus je stelt je FTP-programma maar één keer in. Er draait niets als je hem niet gebruikt: na een uur zonder activiteit gaat hij vanzelf uit.",
    date: "1 november 2026",
    closing: "Onze altijd actieve gedeelde FTP-servers sluiten op 1 november 2026. Gebruik je die nu? Start je eigen server en wijzig de host in je FTP-programma.",
  },
  steps: {
    heading: "Zo werkt het",
    subtitle: "De eerste keer vier stappen. Daarna start je je server en upload je.",
    items: [
      { id: "open", title: "Open de web-uploader", body: "Ga naar `upload.photohawk.com` en log in, of open het tabblad «Upload» van een galerij." },
      { id: "start", title: "Start je server", body: "Klik bovenaan op «FTP server» en daarna op «Start FTP server». Na ongeveer 30 seconden is hij klaar." },
      { id: "connect", title: "Kopieer het adres en de inloggegevens naar je FTP-programma", body: "Je adres, poort 21, je Photohawk-e-mailadres en wachtwoord. Het adres verandert nooit." },
      { id: "upload", title: "Uploaden", body: "Zet je foto's in de map van de galerij. Na een uur zonder uploads gaat de server uit." },
    ],
    button: "Start je FTP-server",
    buttonNote: "Opent de web-uploader met je FTP-server klaar om te starten.",
  },
  panel: {
    heading: "Wat je ziet",
    body: "Alles staat in het paneel «Your FTP server» in de web-uploader, met een kopieerknop naast elk gegeven.",
    points: [
      { id: "address", text: "Je adres, bijvoorbeeld `swift-heron-4821.ftp.photohawk.com`. Het is voorgoed van jou, dus je stelt het maar één keer in." },
      { id: "ip", text: "Het IP-adres van de server staat er ook. Dat verandert elke keer dat je de server start, dus gebruik waar mogelijk het adres." },
      { id: "countdown", text: "Een aftelling tot de server uitgaat, met «Keep it running» en «Stop»." },
    ],
    alt: "Het paneel “Your FTP server” met Ready in Europa (Ierland), de host swift-heron-4821.ftp.photohawk.com, het IP-adres, poort 21, de gebruikersnaam alex@example.com, passieve modus en FTP over TLS, en een aftelling met de knoppen Keep it running en Stop.",
  },
  details: {
    heading: "Je verbindingsgegevens",
    subtitle: "Vul deze in je FTP-programma in.",
    rows: [
      { id: "host", label: "Host", value: "Je adres, bijv. `swift-heron-4821.ftp.photohawk.com` (of het IP-adres dat je ziet)" },
      { id: "port", label: "Poort", value: "21" },
      { id: "user", label: "Gebruikersnaam", value: "Je Photohawk-e-mailadres" },
      { id: "password", label: "Wachtwoord", value: "Je Photohawk-wachtwoord, of je FTP-wachtwoord (zie hieronder)" },
      { id: "mode", label: "Overdrachtsmodus", value: "Passief" },
      { id: "encryption", label: "Versleuteling", value: "Gewone FTP, of FTPS (expliciete FTP over TLS). Gebruik voor FTPS de hostnaam, niet het IP-adres." },
    ],
    switching: "Gebruikte je Photohawk-FTP al? Je gebruikersnaam, wachtwoord en mappen blijven hetzelfde. Wijzig alleen de host naar je nieuwe adres.",
  },
  password: {
    heading: "Een apart FTP-wachtwoord, als je dat wilt",
    body: "In plaats van je Photohawk-wachtwoord in een FTP-programma op te slaan, klik je in het paneel op «Create FTP password». Het werkt alleen op je FTP-server, met je e-mailadres als gebruikersnaam. Je kunt het kopiëren, een nieuw maken (het oude werkt dan binnen een minuut niet meer) of het op elk moment verwijderen. Je Photohawk-wachtwoord blijft ook gewoon werken.",
  },
  folders: {
    heading: "Je mappen",
    body: "Je ziet dezelfde mappen als voorheen:",
    path: "/Galleries/<gallery>/<collection>",
    points: [
      { id: "single", text: "Een galerij met één collectie is gewoon `/Galleries/<gallery>`." },
      { id: "workspaces", text: "Upload je naar meer dan één werkruimte? Elke werkruimte heeft een eigen map, met de galerijen erin." },
      { id: "create", text: "Maak de galerij en de collecties eerst aan in Photohawk. Je foto's verschijnen binnen enkele ogenblikken in de galerij, net als met de web-uploader." },
    ],
  },
  off: {
    heading: "Hij gaat vanzelf uit",
    body: "Je server gaat na 1 uur zonder activiteit uit, dus je hoeft nergens aan te denken. Het paneel toont een aftelling.",
    points: [
      { id: "keep", text: "«Keep it running» start de aftelling opnieuw zonder dat je iets uploadt." },
      { id: "stop", text: "«Stop» zet hem meteen uit als je klaar bent." },
      { id: "caps", text: "Voor de zekerheid gaat hij ook uit na 12 uur waarin niets wordt geüpload, en uiterlijk na 24 uur." },
      { id: "again", text: "Start hem weer wanneer je wilt, met hetzelfde adres en dezelfde inloggegevens." },
    ],
  },
  regions: {
    heading: "Regio's",
    body: "Laat je server draaien in Europa (Ierland), VS-Oost (N. Virginia) of Azië-Pacific (Maleisië). De dichtstbijzijnde regio wordt voor je gekozen, en je kunt die wijzigen voordat je start.",
  },
  filezilla: {
    heading: "Voorbeeld: FileZilla instellen",
    subtitle: "Andere FTP-programma's gebruiken dezelfde instellingen, soms onder iets andere namen.",
    steps: [
      { id: "site", text: "Open «File → Site Manager» en klik op «New site»." },
      { id: "protocol", text: "Protocol: «FTP – File Transfer Protocol»." },
      { id: "host", text: "Host: je adres, bijv. `swift-heron-4821.ftp.photohawk.com`. Poort: `21`." },
      { id: "tls", text: "Versleuteling: «Use explicit FTP over TLS if available»." },
      { id: "login", text: "Inlogtype: «Normal». Gebruiker: je Photohawk-e-mailadres. Wachtwoord: je FTP-wachtwoord of je Photohawk-wachtwoord." },
      { id: "passive", text: "Kies op het tabblad «Transfer Settings» voor «Passive». Klik op «Connect» en open dan `Galleries`." },
    ],
    tip: "Een grote map versturen? FileZilla verstuurt standaard 2 bestanden tegelijk, en nooit meer dan 10. Je kunt de instelling voor gelijktijdige overdrachten verhogen tot 10 («Settings → Transfers»), of de web-uploader gebruiken, die er veel meer tegelijk verstuurt.",
  },
  trouble: {
    heading: "Als iets niet werkt",
    items: [
      { id: "connect", title: "Geen verbinding", body: "Controleer of het paneel «Ready» aangeeft. Is de hostnaam net aangemaakt, gebruik dan een minuut lang het IP-adres." },
      { id: "listing", title: "Verbonden, maar de mappenlijst blijft hangen", body: "Zet de passieve modus aan." },
      { id: "logins", title: "“Too many failed logins”", body: "Wacht 15 minuten en controleer dan de gebruikersnaam (je e-mailadres) en het wachtwoord." },
    ],
  },
  faq: {
    heading: "Vragen",
    items: [
      {
        q: "Waarom ging mijn server uit?",
        a: "Hij gaat uit na een uur zonder activiteit, zodat hij nooit draait als je hem niet gebruikt. Start hem gewoon opnieuw: het adres en de inloggegevens blijven hetzelfde. Wil je hem laten draaien zonder te uploaden, klik dan op «Keep it running». Hij gaat ook uit na 12 uur waarin niets wordt geüpload, en uiterlijk na 24 uur.",
      },
      {
        q: "Kunnen twee mensen tegelijk een server draaien?",
        a: "Ja. Iedereen in je werkruimte kan een eigen server starten, één per persoon, op een eigen adres. Alleen jij kunt inloggen op die van jou.",
      },
      {
        q: "Is het veilig?",
        a: "Ja. Met FTPS (FTP over TLS) op je hostnaam is de verbinding versleuteld met een vertrouwd certificaat. Alleen jouw inloggegevens werken op je server, en na herhaalde mislukte inlogpogingen blokkeert de server die verbinding 15 minuten.",
      },
      {
        q: "Betaal ik extra?",
        a: "Nee. Je eigen FTP-server zit bij alle abonnementen inbegrepen.",
      },
      {
        q: "Zit het bij alle abonnementen?",
        a: "Ja, ook bij Lite. Voor dagelijks uploaden zijn de web-uploader en Photohawk Studio sneller; je eigen FTP-server is er als FTP echt nodig is.",
      },
      {
        q: "Wat gebeurt er met de oude gedeelde FTP?",
        a: "De altijd actieve gedeelde FTP-servers sluiten op 1 november 2026 en werken tot die tijd zoals altijd. Wil je FTP blijven gebruiken, start dan je eigen server en wijzig de host in je FTP-programma naar je nieuwe adres. Je gebruikersnaam, wachtwoord en mappen blijven hetzelfde.",
      },
    ],
  },
  cta: {
    heading: "Klaar wanneer jij dat bent",
    body: "Gebruik voor je dagelijkse uploads de web-uploader. Is FTP echt nodig, dan is je eigen server maar een klik verwijderd.",
    primary: "Open de web-uploader",
    secondary: "Start je FTP-server",
  },
};

export default nl;
